import { createWireStore, RouterState } from '@oneappexchange/lwc-wired';
import { createRouterStore } from '@oneappexchange/lwc-wired-router';
import { InitI18nLinksOptions, initializeI18nLinks } from 'i18n/link';
import { LightningElement } from 'lwc';
import { NavigateOptions } from 'navigo';
import { initializePageLinks } from 'page/link';
import { IS_DEVELOPMENT } from '~/modules/config';
import { isExternalAgentDiscoveryExperienceAvailable } from '~/modules/helpers/externalAgentSearchHelper';
import {
    EXPLORE_BASE_ROUTE,
    LEARN_BASE_ROUTE,
    Routes,
    TRIALS_BASE_ROUTE,
    WORDPRESS_STATIC_PAGE_SLUGS,
    WORDPRESS_STATIC_PAGE_SLUGS_JP,
} from '~/modules/routes';
import { scrollToTop } from '~/modules/utilities/browser';
import { isOrgPickerEnabled } from '~/modules/utilities/orgPickerPilot';
import { extractURLComponents } from '~/modules/utilities/route_utils';
import { logger } from '../utilities/logger';

type ComponentRouteMap = Record<
    string,
    () => Promise<{
        default: typeof LightningElement;
    }>
>;

const ORG_ID_QUERY_PARAM = 'orgId';

// window.stores is injected synchronously into the HTML before any JS runs, so this is
// safe to read at module load time (unlike siteConfigStore which is hydrated later).
// A missing domain is not enough to infer an AppExchange experience.
const domain = window.stores?.SITE_CONFIG?.domain;
const isAgentExchange = domain === 'agentexchange';
const isAppExchangeDomain = domain === 'appexchange' || domain === 'appexchange-jp';

// Add additional routes here
const routes: ComponentRouteMap = {
    '': () => import('pages/home'),
    [Routes.HOME]: () => import('pages/home'),
    [Routes.SEARCH]: () => import('pages/search'),
    [Routes.EXPLORE]: () => import('pages/explore'),
    [Routes.COLLECTION]: () => import('pages/collections'),
    [Routes.COLLECTIONS_HOME]: () => import('pages/collections'),
    [Routes.CALLBACK]: () => import('pages/callback'),
    [Routes.LOGIN]: () => import('pages/login'),
    [Routes.SIGN_UP]: () => import('pages/signup'),
    [Routes.ERROR]: () => import('pages/error'),
    [Routes.NOT_FOUND]: () => import('pages/error'),
};

// Standalone routes render without the site header/footer — a bare container, like the login
// page. A route is added here next to its registration, under the same gate, so a route that
// isn't registered can never be treated as standalone (otherwise the not-found error page that
// renders in its place would inherit the bare layout).
const STANDALONE_ROUTES = new Set<string>();

if (!isAgentExchange) {
    routes[Routes.LEARN_HOME] = () => import('pages/learnHome');
    routes[Routes.LEARN] = () => import('pages/learn');
    routes[Routes.LISTING] = () => import('pages/listingDetailWrapper');
    routes[Routes.CONSULTANT_LISTING] = () => import('pages/listingDetailWrapper');
    routes[Routes.FAVORITE_LISTINGS] = () => import('pages/favoritesPage');
    routes[Routes.TRIALS] = () => import('pages/try');
    routes[Routes.TRIALS_CONFIRMATION] = () => import('pages/try');
    routes[Routes.OFFER_REQUEST] = () => import('pages/offerRequest');
}

if (isAppExchangeDomain) {
    routes[Routes.CONSULTING_HOME] = () => import('pages/consulting');
}

if (isExternalAgentDiscoveryExperienceAvailable()) {
    routes[Routes.SKILLS] = () => import('pages/skills');
}

if (isAgentExchange) {
    routes[Routes.CLAUDEFORCE_BETA_ACCESS] = () => import('pages/claudeforceBetaAccess');
    routes[Routes.JAPANESE_HOME] = () => import('pages/collections');
    routes[Routes.JAPANESE_SEARCH] = () => import('pages/search');
    routes[Routes.JAPANESE_EXPLORE] = () => import('pages/explore');
    routes[Routes.JAPANESE_LOGIN] = () => import('pages/login');
    routes[Routes.JAPANESE_SIGN_UP] = () => import('pages/signup');
    routes[Routes.JAPANESE_CALLBACK] = () => import('pages/callback');
    WORDPRESS_STATIC_PAGE_SLUGS.forEach((slug) => {
        routes[slug] = () => import('pages/collections');
    });
    WORDPRESS_STATIC_PAGE_SLUGS_JP.forEach((slug) => {
        routes[slug] = () => import('pages/collections');
    });
}

// English-only: the org picker is not registered for JP (or any non-EN locale), matching the
// header's EN gate so a JP user can't deep-link /select-org. isOrgPickerEnabled() also honors
// pilot enrollment (~/modules/utilities/orgPickerPilot), which must be written to localStorage
// before this module first evaluates.
if (isOrgPickerEnabled()) {
    routes[Routes.SELECT_ORG] = () => import('pages/selectOrg');
    STANDALONE_ROUTES.add(Routes.SELECT_ORG);
}

// Server in-place error render: serveErrorPage seeds window.stores.ERROR.code and returns the
// SPA shell at the original URL with the error status. When that URL also matches a real client
// route (e.g. explore/:bucket, learn/:slug), Navigo would otherwise mount the real page on
// hydration and discard the server's error render. Use a consume-once latch so the FIRST route
// resolution (bootstrap only) loads pages/error in place, without changing the URL. The latch
// self-clears after the first resolution, so later client-side navigate() and browser Back/Forward
// resolve real routes normally. The error page loads through its own lazy () => import('pages/error')
// chunk — the same route-level code splitting used by every other page.
let pendingBootstrapError = !!window.stores?.ERROR?.code;

const exported = createRouterStore({
    routes: new Proxy(routes, {
        get(target, prop: string) {
            const realLoader = target[prop];
            if (!realLoader) {
                return realLoader;
            }
            // Wrap each loader to check the latch on first invocation. When pendingBootstrapError is
            // true, consume it (set false) and return the error page loader; otherwise return the real
            // loader. Because the latch is checked at *call* time (not read time), and cleared after
            // the first call, only the bootstrap resolution sees the error page — every later navigation
            // (including Back/Forward to the same route) resolves the real page.
            return () => {
                if (pendingBootstrapError) {
                    pendingBootstrapError = false;
                    return import('pages/error');
                }
                return realLoader();
            };
        },
    }),
    storeName: 'routerStore',
    developmentMode: IS_DEVELOPMENT,
    routeLoadDurationCallback: (_, duration) => {
        logger.metric(
            'ept',
            window.location.href.replace('https://', '').replace('http://', '').replace(window.location.host, ''),
            duration,
        );
    },
});

export const routerStore = exported.routerStore;

export const testIsNavigoLink = exported.testIsNavigoLink;

// Strip a `/jp` prefix so the existing whitelist checks match JP-dual-route paths.
// Returns `pathName` unchanged when FF is off or the path is not a /jp route.
function stripJpPrefix(pathName: string): string {
    if (!isAgentExchange) {
        return pathName;
    }
    if (pathName === 'jp') {
        return '';
    }
    if (pathName.startsWith('jp/')) {
        return pathName.slice('jp/'.length);
    }
    return pathName;
}

// Do server side navigation on the home page and client side navigation on the listing detail, search and explore pages
// TODO: This can be removed now that the homepage is in Japanese
export function navigate(to: string, options?: NavigateOptions | undefined) {
    const pathName = extractURLComponents(to)?.pathName || '';
    const normalizedPath = stripJpPrefix(pathName);
    if (
        normalizedPath === Routes.LISTING ||
        normalizedPath === Routes.SEARCH ||
        normalizedPath === Routes.ERROR ||
        normalizedPath === Routes.LOGIN ||
        normalizedPath === Routes.SIGN_UP ||
        normalizedPath === Routes.CALLBACK ||
        normalizedPath === Routes.OFFER_REQUEST ||
        normalizedPath === Routes.CLAUDEFORCE_BETA_ACCESS ||
        normalizedPath === Routes.CONSULTING_HOME ||
        normalizedPath === Routes.SELECT_ORG ||
        normalizedPath.startsWith(TRIALS_BASE_ROUTE) ||
        normalizedPath.startsWith(EXPLORE_BASE_ROUTE) ||
        normalizedPath.startsWith(LEARN_BASE_ROUTE)
    ) {
        return exported.navigate(to, options);
    }
    return window.location.assign(to);
}

// Scroll to top and update page title & description when URL or listingId changes
routerStore.subscribe(scrollToTop, selectUrl);
routerStore.subscribe(scrollToTop, selectListingIdQueryParam);

// Connect page-link instances to our navigate function
initializePageLinks(navigate, testIsNavigoLink);
// enable custom link parsing to events
const i18nOptions: InitI18nLinksOptions = {
    useCustomEvents: true,
};
initializeI18nLinks(navigate, testIsNavigoLink, i18nOptions);

export const WireRouter = createWireStore(routerStore);

export function selectUrl(state: RouterState = routerStore.getState()) {
    return state.url;
}

export function selectQueryString(state: RouterState = routerStore.getState()) {
    return state.queryString;
}

export function selectQueryParams(state: RouterState) {
    return state.params || {};
}

export function selectQueryParam(state: RouterState, param: string, defaultValue?: string): string | undefined {
    const queryParams = selectQueryParams(state);
    const result = queryParams[param];
    return result || defaultValue;
}

export function selectListingIdQueryParam(state: RouterState): string | undefined {
    return selectQueryParam(state, 'listingId');
}

export function selectPreviewIdQueryParam(state: RouterState): string | undefined {
    return selectQueryParam(state, 'axwp-preview-id');
}

export function selectOrgIdQueryParam(state: RouterState) {
    return selectQueryParam(state, ORG_ID_QUERY_PARAM);
}

export function selectScrollToQueryParam(state: RouterState): string | undefined {
    return selectQueryParam(state, 'scrollTo');
}

export function selectMatch(state: RouterState) {
    const { url, queryString, hashString, data, params } = state;
    return { url, queryString, hashString, data, params };
}

export function selectIsStandalone(state: RouterState): boolean {
    return STANDALONE_ROUTES.has(state.url);
}
